import React, { useEffect, useState } from "react";
import { CheckCircle2, AlertTriangle, Loader2, Rocket, RefreshCw } from "lucide-react";

/**
 * DeployStatusCard — compares the build SHA of the environment the admin
 * is CURRENTLY browsing (preview, where you edit code) with the build
 * SHA of the live PRODUCTION deployment (https://camp-compass.uk).
 *
 *   • If both are the SAME → green "In sync" pill, no further action.
 *   • If preview is AHEAD → big orange "Deploy needed" CTA so the user
 *     knows there's work waiting to go live.
 *   • If production /api/version is 404 / unreachable → assumes prod
 *     is on an older build that pre-dates this endpoint, so still
 *     shows the deploy CTA (covers the very first deploy of /version
 *     itself).
 */
const PRODUCTION_URL = "https://camp-compass.uk";

async function fetchVersion(baseUrl) {
  try {
    const r = await fetch(`${baseUrl}/api/version`, { cache: "no-store" });
    if (!r.ok) return { ok: false, status: r.status };
    const data = await r.json();
    return { ok: true, ...data };
  } catch {
    return { ok: false, status: 0 };
  }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DeployStatusCard() {
  const [preview, setPreview] = useState(null);
  const [prod, setProd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    // Same-origin: this is the env the admin is currently on.
    const previewBase = process.env.REACT_APP_BACKEND_URL || "";
    const [p, pr] = await Promise.all([
      fetchVersion(previewBase),
      fetchVersion(PRODUCTION_URL),
    ]);
    setPreview(p);
    setProd(pr);
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => { load(); }, []);

  const onProduction = typeof window !== "undefined"
    && window.location.host === "camp-compass.uk";

  if (loading) {
    return (
      <div className="bg-white border hairline p-5 sm:p-6" data-testid="deploy-status-card">
        <div className="flex items-center gap-2 text-sm text-[#525252]">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking deploy status…
        </div>
      </div>
    );
  }

  const previewSha = preview?.git_sha;
  const prodSha = prod?.git_sha;
  const inSync = preview?.ok && prod?.ok && previewSha && previewSha === prodSha;
  const prodMissing = !prod?.ok; // 404, timeout, etc.
  const deployNeeded = !inSync;

  return (
    <div
      className={`border-2 p-5 sm:p-6 ${
        inSync ? "bg-[#dcfce7] border-[#16a34a]"
               : "bg-[#fef3c7] border-[#f59e0b]"
      }`}
      data-testid="deploy-status-card"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="eyebrow mb-1 text-[#1c1917]">Deployment status</div>
          <div className="flex items-center gap-2">
            {inSync ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-[#16a34a]" />
                <h3 className="font-display text-xl sm:text-2xl tracking-tight text-[#14532d]">
                  Preview is in sync with production
                </h3>
              </>
            ) : (
              <>
                <AlertTriangle className="w-5 h-5 text-[#b45309]" />
                <h3 className="font-display text-xl sm:text-2xl tracking-tight text-[#7c2d12]">
                  Deploy needed — preview is ahead
                </h3>
              </>
            )}
          </div>
          {onProduction ? (
            <p className="text-xs text-[#525252] mt-2">
              You&apos;re currently viewing the production site. To work on new
              changes, switch to the preview environment.
            </p>
          ) : null}
        </div>
        <button
          type="button"
          onClick={load}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-[0.2em] text-[#1c1917] hover:underline disabled:opacity-50"
          data-testid="deploy-status-refresh"
        >
          {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
        <div className="bg-white border hairline p-3" data-testid="deploy-status-preview">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-1">
            Preview · current edit environment
          </div>
          <div className="mono text-sm text-[#1c1917] truncate">
            {preview?.git_short || preview?.git_sha?.slice(0, 8) || "—"}
          </div>
          <div className="text-xs text-[#525252] mt-1 truncate" title={preview?.commit_message}>
            {preview?.commit_message || "—"}
          </div>
          <div className="mono text-[10px] text-[#a3a3a3] mt-1">
            committed {fmtDate(preview?.commit_at)}
          </div>
        </div>
        <div className="bg-white border hairline p-3" data-testid="deploy-status-prod">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-[#525252] mb-1">
            Production · camp-compass.uk
          </div>
          {prodMissing ? (
            <div className="mono text-sm text-[#7c2d12]">
              Older build (pre-version endpoint)
            </div>
          ) : (
            <>
              <div className="mono text-sm text-[#1c1917] truncate">
                {prod?.git_short || prod?.git_sha?.slice(0, 8) || "—"}
              </div>
              <div className="text-xs text-[#525252] mt-1 truncate" title={prod?.commit_message}>
                {prod?.commit_message || "—"}
              </div>
              <div className="mono text-[10px] text-[#a3a3a3] mt-1">
                committed {fmtDate(prod?.commit_at)}
              </div>
            </>
          )}
        </div>
      </div>

      {deployNeeded ? (
        <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="deploy-status-cta">
          <div className="inline-flex items-center gap-2 bg-[#1c1917] text-white px-3 py-2 text-sm">
            <Rocket className="w-4 h-4 text-[#fde047]" />
            <span>Click <b>Deploy → Redeploy existing</b> in the Emergent chat to push these changes live.</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
